import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import './components.css';

export interface SidebarItem { key: string; label: ReactNode; badge?: ReactNode; href: string; active?: boolean }
export interface SidebarSection { label?: ReactNode; items: SidebarItem[] }

export interface AppShellProps {
  brand: { initial: string; title: ReactNode; subtitle?: ReactNode; onClick?: () => void; dark?: boolean };
  sections: SidebarSection[];
  footer?: ReactNode;
  renderLink: (item: SidebarItem, className: string, children: ReactNode) => ReactNode;
  children: ReactNode;
}

/** Shell com sidebar (mockups): marca, navegação em seções, rodapé (uso + usuário). No celular vira menu. */
export function AppShell({ brand, sections, footer, renderLink, children }: AppShellProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`k-shell ${brand.dark ? 'k-shell--dark-sidebar' : ''}`}>
      <button className="k-shell__menu" aria-label="Menu" onClick={() => setOpen((v) => !v)}>
        <span /><span /><span />
      </button>
      {open && <div className="k-shell__scrim" onClick={() => setOpen(false)} />}
      <aside className={`k-sidebar ${open ? 'k-sidebar--open' : ''}`}>
        <button type="button" className="k-brand" onClick={brand.onClick}>
          <span className="k-brand__mark">{brand.initial}</span>
          <span className="k-brand__text">
            <span className="k-brand__title">{brand.title}</span>
            {brand.subtitle && <span className="k-brand__subtitle">{brand.subtitle}</span>}
          </span>
        </button>
        <nav className="k-nav" onClick={() => setOpen(false)}>
          {sections.map((s, i) => (
            <div key={i} className="k-nav__section">
              {s.label && <div className="k-nav__label">{s.label}</div>}
              {s.items.map((it) =>
                renderLink(
                  it,
                  `k-nav__item ${it.active ? 'k-nav__item--active' : ''}`,
                  <>
                    <span className="k-nav__dot" />
                    <span className="k-nav__text">{it.label}</span>
                    {it.badge != null && <span className="k-nav__badge">{it.badge}</span>}
                  </>,
                ),
              )}
            </div>
          ))}
        </nav>
        {footer && <div className="k-sidebar__footer">{footer}</div>}
      </aside>
      <main className="k-main">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="k-page-header">
      <div>
        <h1 className="k-page-title">{title}</h1>
        {subtitle && <div className="k-page-subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="k-page-actions">{actions}</div>}
    </header>
  );
}

export function UsageMeter({ label, pct }: { label: ReactNode; pct: number }) {
  const p = Math.max(0, Math.min(100, pct));
  return (
    <div className="k-usage">
      <div className="k-usage__row"><span>{label}</span><strong>{Math.round(p)}%</strong></div>
      <div className="k-usage__track"><div className={`k-usage__bar ${p >= 90 ? 'k-usage__bar--warn' : ''}`} style={{ width: `${p}%` }} /></div>
    </div>
  );
}

export function UserBadge({ initials, name, role, onClick }: { initials: string; name: ReactNode; role: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" className="k-userbadge" onClick={onClick}>
      <span className="k-avatar">{initials}</span>
      <span className="k-userbadge__text"><span className="k-userbadge__name">{name}</span><span className="k-userbadge__role">{role}</span></span>
    </button>
  );
}

export function EmptyState({ title, description, action }: { title: ReactNode; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="k-empty">
      <div className="k-panel-title">{title}</div>
      {description && <p className="k-muted">{description}</p>}
      {action}
    </div>
  );
}

/** Barras cinza animadas no lugar do conteúdo enquanto a primeira carga não chega. */
export function Skeleton({ lines = 3, height = 14, width, style }: { lines?: number; height?: number; width?: string | number; style?: CSSProperties }) {
  return (
    <div className="k-skeleton" aria-hidden style={style}>
      {Array.from({ length: lines }).map((_, i) => <span key={i} className="k-skeleton__bar" style={{ height, width: width ?? (i === lines - 1 && lines > 1 ? '60%' : '100%') }} />)}
    </div>
  );
}

/**
 * Estado de carregamento de página inteira: cabeçalho (se houver) + cartões de esqueleto.
 * Use no lugar do conteúdo enquanto a query principal está em `isLoading`, para a tela não "piscar" vazia antes do dado chegar.
 */
export function PageLoading({ title, cards = 2, rows = 4 }: { title?: ReactNode; cards?: number; rows?: number }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="k-page-loading">
      {title && <div className="k-page-loading__title"><Skeleton lines={1} height={28} width="40%" /></div>}
      <div className="k-page-loading__grid">
        {Array.from({ length: cards }).map((_, i) => <div key={i} className="k-card k-card--padded"><Skeleton lines={rows} /></div>)}
      </div>
    </div>
  );
}
